/*
 * Nimall public product shelf rotation v1.
 *
 * Rotates only buyer-facing merchandising and recommendation shelves. Search
 * results, collection grids, cart lines, prices and availability are untouched.
 * No visitor identifier, storage, cookie or behavior event is read or written.
 */
(function (window, document) {
  'use strict';

  var VERSION = 'nimall-global-product-rotation-v1';
  var WINDOW_MS = 30 * 60 * 1000;
  var REFRESH_DELAY_MS = 1500;
  var ROOT_SELECTOR = [
    '[data-product-block]',
    '[data-product-tab-block]',
    '[data-recommendations-block]',
    '[data-recently-viewed-block]',
    '.y01-home .y01-shelf'
  ].join(',');
  var CONTAINER_SELECTOR = [
    '.products-grid',
    '.products-flex',
    '.products-carousel',
    '.y01-product-rail'
  ].join(',');
  var stateByContainer = new WeakMap();
  var mutationTimer = 0;
  var mobileViewport = window.matchMedia('(max-width: 767px)');
  var pendingMobileRoots = new Set();

  function hash32(value) {
    var hash = 0x811c9dc5;
    var index;
    for (index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
  }

  function currentWindowStart() {
    return Math.floor(Date.now() / WINDOW_MS) * WINDOW_MS;
  }

  function marketKey() {
    var country = window.Shopify && window.Shopify.country;
    return typeof country === 'string' && country ? country.toUpperCase() : 'GLOBAL';
  }

  function rootKey(root) {
    if (root.classList.contains('y01-shelf')) {
      var shelfLink = root.querySelector('.y01-section-heading a');
      return 'wap-home|' + (shelfLink ? shelfLink.pathname : root.id);
    }
    var section = root.closest('[id^="shopify-section-"]');
    return [
      section ? section.id : '',
      root.id || '',
      root.getAttribute('sectionId') || '',
      root.getAttribute('data-collection') || '',
      window.location.pathname
    ].join('|');
  }

  function productKey(card) {
    var item = card.querySelector('[data-product-id]');
    var link = card.querySelector('a[href*="/products/"]');
    return (item && item.getAttribute('data-product-id'))
      || card.getAttribute('data-product-id')
      || (link && link.getAttribute('href'))
      || '';
  }

  function directProductCards(container) {
    return Array.prototype.filter.call(container.children, function (child) {
      return child.classList
        && (child.classList.contains('product') || child.classList.contains('y01-product-card'))
        && !child.classList.contains('slick-cloned')
        && productKey(child);
    });
  }

  function carouselIsManaged(container) {
    return container.classList.contains('slick-initialized')
      || Boolean(container.closest('.slick-track'));
  }

  function keepsCollectionOrder(root) {
    if (!root) return false;
    if (root.getAttribute('data-collection') === 'new-arrival') return true;
    if (!root.classList.contains('y01-shelf')) return false;
    var link = root.querySelector('.y01-section-heading a');
    return Boolean(link && /\/collections\/new-arrival\/?$/.test(link.pathname));
  }

  function orderElements(elements, identity, windowStart) {
    if (keepsCollectionOrder(document.getElementById(identity))) return elements.slice();
    return elements.slice().sort(function (left, right) {
      var leftKey = [VERSION, marketKey(), windowStart, identity, productKey(left)].join('|');
      var rightKey = [VERSION, marketKey(), windowStart, identity, productKey(right)].join('|');
      var difference = hash32(leftKey) - hash32(rightKey);
      return difference || productKey(left).localeCompare(productKey(right));
    });
  }

  function rotateContainer(container, root, windowStart) {
    if (container.hasAttribute('data-ncb-managed') || keepsCollectionOrder(root) || carouselIsManaged(container)) return;
    var cards = directProductCards(container);
    if (cards.length < 2) return;
    var identity = rootKey(root);
    var signature = [windowStart, identity, cards.map(productKey).sort().join(',')].join('|');
    if (stateByContainer.get(container) === signature) return;

    orderElements(cards, identity, windowStart).forEach(function (card) {
      container.appendChild(card);
    });
    stateByContainer.set(container, signature);
    container.setAttribute('data-nimall-product-rotation', VERSION);
    container.setAttribute('data-nimall-product-rotation-window', String(windowStart));
    if (root.classList.contains('y01-shelf')) {
      container.scrollLeft = 0;
      window.requestAnimationFrame(function () { container.scrollLeft = 0; });
    }
  }

  function rotateRoots(roots) {
    var windowStart = currentWindowStart();
    // Read mobile visibility together before moving cards. Hidden PC shelves
    // remain untouched until the breakpoint changes or they become visible.
    var visibleRoots = mobileViewport.matches
      ? roots.filter(function (root) { return root.isConnected && root.getClientRects().length; })
      : roots;
    visibleRoots.forEach(function (root) {
      root.querySelectorAll(CONTAINER_SELECTOR).forEach(function (container) {
        rotateContainer(container, root, windowStart);
      });
    });
  }

  function rotateAll() {
    pendingMobileRoots.clear();
    rotateRoots(Array.from(document.querySelectorAll(ROOT_SELECTOR)));
    document.documentElement.setAttribute('data-nimall-product-rotation', VERSION);
  }

  function containsProduct(node) {
    return node.nodeType === 1 && (node.matches('.product, .y01-product-card')
      || Boolean(node.querySelector('.product, .y01-product-card')));
  }

  function scheduleRotation(mutations) {
    // Countdown text, icons, carousel dots and language feedback do not alter
    // the product set. On phones, revisit only shelves whose cards changed.
    if (mobileViewport.matches && Array.isArray(mutations)) {
      mutations.forEach(function (mutation) {
        var nodes = Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes));
        if (!nodes.some(containsProduct)) return;
        var target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
        var root = target && target.closest(ROOT_SELECTOR);
        if (root) pendingMobileRoots.add(root);
        Array.from(mutation.addedNodes).forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches(ROOT_SELECTOR)) pendingMobileRoots.add(node);
          node.querySelectorAll(ROOT_SELECTOR).forEach(function (addedRoot) { pendingMobileRoots.add(addedRoot); });
        });
      });
      if (!pendingMobileRoots.size) return;
      if (mutationTimer) window.clearTimeout(mutationTimer);
      mutationTimer = window.setTimeout(function () {
        var roots = Array.from(pendingMobileRoots);
        pendingMobileRoots.clear();
        rotateRoots(roots);
      }, 40);
      return;
    }
    if (mutationTimer) window.clearTimeout(mutationTimer);
    mutationTimer = window.setTimeout(rotateAll, 40);
  }

  function scheduleNextWindow() {
    var delay = currentWindowStart() + WINDOW_MS - Date.now() + REFRESH_DELAY_MS;
    window.setTimeout(function () {
      rotateAll();
      scheduleNextWindow();
    }, Math.max(0, delay));
  }

  function initialize() {
    rotateAll();
    scheduleNextWindow();
    mobileViewport.addEventListener('change', rotateAll);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') rotateAll();
    });
    if ('MutationObserver' in window) {
      var main = document.querySelector('#MainContent') || document.body;
      new MutationObserver(scheduleRotation).observe(main, {
        childList: true,
        subtree: true
      });
    }
    document.addEventListener('shopify:section:load', rotateAll);
  }

  window.NimallPublicProductRotation = Object.freeze({
    version: VERSION,
    orderElements: function (elements, identity) {
      return orderElements(elements, identity, currentWindowStart());
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})(window, document);

